import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { rebindExistingPush } from "@/lib/supportPush";

const Context = createContext(null);

export const SupportNotificationsProvider = ({ user, children }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(null);
  const pending = useRef(false);

  const refresh = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    try {
      const { data } = await api.get("/support/notifications");
      if (seen.current) {
        data.items.filter(item => !item.is_read && !seen.current.has(item.id))
          .slice(0, 3).reverse().forEach(item => {
            toast.custom(id => (
              <button type="button" onClick={() => { toast.dismiss(id); navigate(item.url); }}
                      className="block text-left max-w-sm w-full bg-white border-l-4 border-[#C5B39B] shadow-xl p-4 text-[#1A1A1A]"
                      data-testid={`support-live-notice-${item.id}`}>
                <strong className="block text-sm">{item.title}</strong>
                <span className="text-xs text-[#707070]">{item.body}</span>
              </button>
            ));
          });
      }
      seen.current = new Set(data.items.map(item => item.id));
      setItems(data.items); setUnread(data.unread);
    } catch { /* The in-app list remains available on the next poll. */ }
    finally { pending.current = false; }
  }, [navigate]);

  useEffect(() => {
    if (!user?.id || user?.impersonation) return undefined;
    seen.current = null;
    refresh();
    // Bind an existing browser subscription to the current authenticated user
    // without requesting permission outside a user gesture.
    rebindExistingPush().catch(() => {});
    const timer = window.setInterval(refresh, 20000);
    const onFocus = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", onFocus); };
  }, [user?.id, user?.impersonation, refresh]);

  const markTicketRead = async ticketId => {
    await api.patch(`/support/notifications/read-ticket/${ticketId}`);
    await refresh();
  };

  return <Context.Provider value={{ items, unread, refresh, markTicketRead }}>{children}</Context.Provider>;
};

export const useSupportNotifications = () => useContext(Context);